import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaArrowRight, FaCheck, FaCheckCircle, FaCreditCard, FaHeadset, FaLock, FaShieldAlt } from "react-icons/fa";

export default function PaymentPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session");
    const [ready, setReady] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [status, setStatus] = useState("");

    useEffect(() => {
        setReady(false);
        setStatus("");
        if (!sessionId) return;
        let active = true;
        window.errorCallback = (error) => {
            console.error("Payment gateway error", error);
            if (active) setStatus("The payment could not be completed. Please try again.");
        };
        window.cancelCallback = () => {
            if (active) setStatus("Payment was cancelled. You can try again when ready.");
        };
        const script = document.createElement("script");
        script.src = "https://ap-gateway.mastercard.com/static/checkout/checkout.min.js";
        script.async = true;
        script.setAttribute("data-error", "errorCallback");
        script.setAttribute("data-cancel", "cancelCallback");
        script.onload = () => { if (active) setReady(!!window.Checkout); };
        script.onerror = () => { if (active) setStatus("Unable to load the payment gateway. Please refresh and try again."); };
        document.body.appendChild(script);
        return () => {
            active = false;
            script.remove();
            delete window.errorCallback;
            delete window.cancelCallback;
        };
    }, [sessionId]);

    function startPayment() {
        if (!accepted || !ready || !sessionId || !window.Checkout) return;
        try {
            setStatus("");
            window.Checkout.configure({ session: { id: sessionId } });
            window.Checkout.showPaymentPage();
        } catch (error) {
            console.error("Checkout launch failed", error);
            setStatus("Unable to open secure checkout. Please try again.");
        }
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] text-slate-800">
            <section className="relative mt-16 overflow-hidden bg-[#0B6E8E]">
                <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[#F7B928]/15 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-32 left-0 h-72 w-72 rounded-full bg-[#FF5A1F]/20 blur-3xl" />
                <div className="relative mx-auto max-w-[1536px] px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full borde border-white/20 bg-white/10 px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-[#F7B928]">
                        <FaLock /> Secure Checkout
                    </div>
                    <h1 className="mt-6 text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
                        Complete Your <span className="text-[#F7B928]">Payment</span>
                    </h1>
                    <p className="mt-5 max-w-2xl text-sm leading-7 text-white/80 sm:text-base">
                        You're one step away from completing your booking. Continue to our secure payment gateway with confidence.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white"><FaShieldAlt className="text-[#F7B928]" /> Secure checkout</span>
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white"><FaCreditCard className="text-[#F7B928]" /> Mastercard gateway</span>
                    </div>
                </div>
                <div className="h-1 bg-gradient-to-r from-[#FF5A1F] via-[#F7B928] to-white" />
            </section>

            <main className="mx-auto max-w-[1200px] px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
                <Link to="/" className="mb-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0B6E8E] hover:text-[#FF5A1F]">
                    <FaArrowLeft /> Back to Home
                </Link>
                <div className="grid overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_18px_55px_rgba(11,110,142,0.09)] lg:grid-cols-[0.85fr_1.15fr]">
                    <aside className="relative overflow-hidden bg-[#0B6E8E] p-7 text-white sm:p-10 lg:p-12">
                        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#F7B928]/15 blur-3xl" />
                        <div className="relative">
                            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-2xl text-[#F7B928]"><FaLock /></div>
                            <h2 className="mt-7 text-3xl !text-white tracking-tight sm:text-4xl">Secure <span className="block text-[#F7B928]">Payment</span></h2>
                            <p className="mt-4 text-sm leading-7 text-white/75">Your payment is processed through Mastercard Payment Gateway Services.</p>

                            <div className="mt-9 space-y-7">
                                {[
                                    { icon: FaShieldAlt, title: "Protected checkout", desc: "Continue through the secure payment gateway." },
                                    { icon: FaCreditCard, title: "Trusted payment processing", desc: "Payment is handled through Mastercard Payment Gateway Services." },
                                    { icon: FaHeadset, title: "Need assistance?", desc: "Contact our support team if you need help with your booking." },
                                ].map(({ icon: Icon, title, desc }) => (
                                    <div key={title} className="flex items-start gap-4">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#F7B928]">
                                            <Icon />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold !text-white">
                                                {title}
                                            </h3>
                                            <p className="mt-1 text-xs leading-6 text-white/65">
                                                {desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-10 rounded-2xl border border-white/15 bg-white/10 p-4 text-xs leading-6 text-white/75"><FaLock className="mr-2 inline text-[#F7B928]" /> Please complete payment only through the official secure checkout.</div>
                        </div>
                    </aside>

                    <section className="p-7 sm:p-10 lg:p-12">
                        <div className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F]/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[#0B6E8E]"><span className="h-2 w-2 rounded-full bg-[#FF5A1F]" /> Payment Authorization</div>
                        <h2 className="mt-5 text-3xl font-black tracking-tight text-[#0B6E8E] sm:text-4xl">Ready to Pay?</h2>
                        <div className="mt-4 flex gap-2"><span className="h-1 w-12 rounded-full bg-[#FF5A1F]" /><span className="h-1 w-7 rounded-full bg-[#F7B928]" /><span className="h-1 w-4 rounded-full bg-[#0B6E8E]" /></div>
                        <p className="mt-5 text-sm leading-7 text-slate-500">Please review and accept our policies before continuing to the secure payment page.</p>
                        <div className={`mt-8 rounded-2xl border p-5 transition-colors ${accepted ? "border-[#0B6E8E]/40 bg-[#0B6E8E]/5" : "border-slate-200 bg-[#F8FAFC]"}`}>
                            <div className="flex items-start gap-3">
                                <input id="payment-consent" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#0B6E8E]" />
                                <div><label htmlFor="payment-consent" className="text-sm font-semibold text-slate-800">I agree to the policies below</label>
                                    <p className="mt-2 text-sm leading-6 text-slate-600">I have read and agree to the <Link className="font-bold text-[#0B6E8E] underline underline-offset-2 hover:text-[#FF5A1F]" to="/terms-conditions">Terms & Conditions</Link> and <Link className="font-bold text-[#0B6E8E] underline underline-offset-2 hover:text-[#FF5A1F]" to="/privacy-policy">Privacy Policy</Link>.</p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6" aria-live="polite">
                            {!sessionId ? (
                                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"><strong>Payment Session Missing.</strong> Return to your booking and try again.</div>
                            ) : status ? (
                                <div role="alert" className="rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">{status}</div>
                            ) : ready ? (
                                <div className="flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-800"><FaCheckCircle className="shrink-0" /><span><strong>Secure Checkout Ready.</strong> You can proceed to payment.</span></div>
                            ) : (
                                <div className="flex items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-[#0B6E8E]"><span className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#0B6E8E]/20 border-t-[#0B6E8E]" /> Preparing secure checkout...</div>
                            )}
                        </div>
                        <button type="button" onClick={startPayment} disabled={!sessionId || !ready || !accepted} className="mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-[#FF5A1F] px-6 py-4 text-sm font-extrabold text-white shadow-[0_12px_30px_rgba(255,90,31,0.18)] transition hover:-translate-y-0.5 hover:bg-[#e94c15] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none disabled:hover:translate-y-0">
                            <FaLock /> Proceed to Secure Payment <FaArrowRight />
                        </button>
                        <div className="mt-7 flex flex-wrap items-center justify-center gap-4 border-t border-slate-100 pt-6 text-xs font-semibold text-slate-500"><span className="flex items-center gap-2"><FaCheck className="text-[#FF5A1F]" /> Secure checkout</span><span className="flex items-center gap-2"><FaCreditCard className="text-[#FF5A1F]" /> Mastercard Gateway</span></div>
                    </section>
                </div>
                <p className="mt-7 text-center text-xs leading-6 text-slate-500"><FaShieldAlt className="mr-2 inline text-[#0B6E8E]" /> Payments are processed through Mastercard Payment Gateway Services.</p>
            </main>
        </div>
    );
}
